import { FC, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Prompt } from 'react-router-dom';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import history from 'service/customHistory';
import CodeEditor from 'components/common/CodeEditor/CodeEditor';
import Page from 'components/common/Page/Page';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import * as K from 'common/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { getStringFromError } from 'components/common/CodeEditor/helpers';
import { timezoneMoment } from 'common/util/time';
import PageHeader from './PageHeader';
import {
  ADD_CONFIG_FILE, CONFIG_FILE, DELETE_CONFIG_FILE, FORMAT_CONFIG_FILE,
} from './queries';
import './styles.scss';


type ConfigurationFilePageProps = {
  path: string;
};

const formattedDate = (date: string, timezone: string | null): string => 
  timezoneMoment(timezone)(date).format('MMMM Do YYYY, h:mm:ss a');

const ConfigurationFilePage: FC<ConfigurationFilePageProps> = ({ path }: ConfigurationFilePageProps) => {
  const timezone = useRecoilValue(timezoneState);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const showSuccessAlert = (message: string): void => {
    setPageAlertConfig({ message, variant: PageAlertVariant.SUCCESS, autoHide: true });
  };
  const showErrorAlert = (message: string): void => {
    setPageAlertConfig({ message: clearGraphQLErrorMessage(message), variant: PageAlertVariant.ERROR, autoHide: true });
  };
  const [deleteConfigMutation] = useMutation(DELETE_CONFIG_FILE, {
    onCompleted: () => {
      history.push({
        pathname: K.ROUTE.CONFIG,
      });
    },
    onError: (error: ApolloError) => {
      showErrorAlert(clearGraphQLErrorMessage(error.message));
    },
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [fileContent, setFileContent] = useState<string>('');
  const [inputTouched, setInputTouched] = useState<boolean>(false);
  const [addConfigFile] = useMutation(ADD_CONFIG_FILE, {
    onCompleted: () => {
      setInputTouched(false);
      showSuccessAlert('File saved');
    },
    onError: (error: ApolloError) => {
      showErrorAlert(getStringFromError(error));
    },
  });
  const { data, error: configFileError } = useQuery(CONFIG_FILE, {
    variables: { id: path },
    onCompleted: (fileContentData) => {
      setFileContent(fileContentData?.configFile?.content);
    },
    onError: (fileContentError) => {
      if (fileContentError?.message) {
        showErrorAlert(fileContentError?.message);
      }
    },
    fetchPolicy: 'network-only',
  });

  const [formatConfigFile] = useMutation(FORMAT_CONFIG_FILE, {
    onCompleted: (formattedData) => {
      if (formattedData?.formatConfigFile) {
        setFileContent(formattedData?.formatConfigFile);
        showSuccessAlert('File formatted');
      }
    },
    onError: (error: ApolloError) => {
      showErrorAlert(getStringFromError(error));
    },
  });
  const onPrettyPrint = (): void => {
    formatConfigFile({
      variables: {
        id: path,
        content: fileContent,
      },
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onCodeChange = (editor: any, _data: any, value: string): void => {
    setFileContent(value);
    if (!inputTouched) {
      setInputTouched(true);
    }
  };

  const onSaveFile = (): void => {
    addConfigFile({
      variables: {
        name: path,
        content: fileContent,
      },
    });
  };

  const performConfigDeletion = (): void => {
    // To prevent additional dialog display.
    setInputTouched(false);

    deleteConfigMutation({
      variables: {
        id: path,
      },
    });
  };

  const handleFileSaveViaShortCut = (): void => {
    if (inputTouched) {
      onSaveFile();
    } else {
      showErrorAlert('There is nothing to save');
    }
  };

  if (configFileError) {
    return null;
  }

  const readOnly = !!data?.configFile?.readOnly;

  return (
    <Page docTitle={`Configuration file: ${path}`} pageModifier="FilePageRoot" hasBackground>
      <PageHeader
        name={path}
        isWriteDisabled={readOnly}
        onPrettyPrint={onPrettyPrint}
        onDeleteFile={(): void => setShowConfirmDialog(true)}
        onSaveFile={onSaveFile}
        inputTouched={inputTouched}
      />
      <div className="FilePage__MetaBar">
        <div className="FilePage__MetaInfo">
          {data?.configFile?.createdDate && (
          <div>
            <strong>Created: </strong>
            {formattedDate(data?.configFile?.createdDate, timezone)}
          </div>
          )}
          {data?.configFile?.modifiedDate && (
          <div>
            <strong>Last Modified: </strong>
            {formattedDate(data?.configFile?.modifiedDate, timezone)}
          </div>
          )}
          {data?.configFile?.content?.length && (
          <div>
            <strong>Length: </strong>
            {data?.configFile?.content?.length}
          </div>
          )}
        </div>
      </div>
      <CodeEditor
        className="FilePage__CodeEditor"
        value={fileContent}
        options={{ readOnly }}
        onChange={onCodeChange}
        onSave={handleFileSaveViaShortCut}
      />
      <Prompt
        when={inputTouched}
        message="Are you sure you want to leave? Changes you made may not be saved."
      />
      <ConfirmDialog
        title="Please Confirm"
        text={`Are you sure you want to delete the configuration file "${path}"?`}
        show={showConfirmDialog}
        close={(): void => setShowConfirmDialog(false)}
        onSubmit={performConfigDeletion}
      />
    </Page>
  );
};

export default ConfigurationFilePage;
